import { yupResolver } from '@hookform/resolvers/yup';
import { Box, SvgIcon, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import DateRangePickerController from '@Components/DateRangePickerController';
import {
  Button,
  ConfirmationMessageModal,
  Modal,
  ModalAlert,
  FullScreenLoader,
  TextFieldWrapper,
  ScrollToTop,
  DateState,
} from '@Components/index';
import TimePickerCustom from '@Components/TimePickerCustom/TimePickerCustom.component';
import { CommonConstants, Icons } from '@Constants/index';
import {
  fromDateToDashedString,
  getDateFromString,
  getExcludedDayRanges,
  getStringFromDate,
} from '@Containers/Settings/Settings.utils';
import {
  useAddStateTsrMutation,
  useEditStateTsrMutation,
  useGetStateTsrQuery,
} from '@Services/settings.service';
import { BaseAlertProps, DialerCrudTypes } from '@Typings/global.types';
import useAlert from '@Utils/hooks/useAlert.hook';

import {
  StateTsrModalProps,
  StateTsrModalInputs,
  StateTsrModalData,
} from './StateTsrModal.types';
import StateTsrModalSchema from './StateTsrModalSchema';

const StateTsrModal: React.FunctionComponent<StateTsrModalProps> = (
  props: StateTsrModalProps
): JSX.Element => {
  /** Hooks */
  const { t } = useTranslation();
  const { triggerAlert } = useAlert();

  /** Props */
  const { stateTsrId, crudType, setCrudType, deleteBtnOnClick } = props;

  /** States */
  const [alertState, setAlertState] = useState<BaseAlertProps>({
    openAlert: false,
  });
  const [modalAlertState, setModalAlertState] = useState<BaseAlertProps>({
    openAlert: false,
  });
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [dateStates, setDateStates] = useState<DateState[]>([
    {
      startDate: undefined,
      endDate: undefined,
      key: 'selection',
    },
  ]);

  /**
   * Creates new date range picker for excluded days
   */
  const addDateRangePicker = () => {
    setDateStates((prevDateStates) => [
      ...prevDateStates,
      {
        startDate: undefined,
        endDate: undefined,
        key: 'selection',
      },
    ]);
  };

  // const removeDateRangePicker = (index: number) => {
  //   setDateStates((prevDateStates) => {
  //     const updatedDateStates = [...prevDateStates];
  //     updatedDateStates.splice(index, 1);
  //     return updatedDateStates;
  //   });
  // };

  /** FORM METHODS */
  const methods = useForm<StateTsrModalInputs>({
    resolver: yupResolver(StateTsrModalSchema()),
    mode: 'onChange',
    shouldUnregister: true,
  });

  /** Returns Modal Header Text   */
  const getHeaderText = (): string => {
    switch (crudType) {
      case DialerCrudTypes.Add: {
        return t('adminSettings.Add State TSR');
      }
      case DialerCrudTypes.Edit: {
        return t('adminSettings.Edit State TSR');
      }
      case DialerCrudTypes.View: {
        return t('adminSettings.View State TSR');
      }
      default: {
        return '';
      }
    }
  };

  /** RTK Query Hooks for State TSR */
  const [editStateTsr, editStateTsrMethods] = useEditStateTsrMutation();
  const [addStateTsr, addStateTsrMethods] = useAddStateTsrMutation();
  const {
    data: stateTsrData,
    isLoading,
    isFetching,
    error: stateTsrModalError,
  } = useGetStateTsrQuery(stateTsrId, {
    skip: !stateTsrId,
  });

  /** Loading State */
  const loading =
    isLoading ||
    isFetching ||
    editStateTsrMethods.isLoading ||
    addStateTsrMethods.isLoading;

  /** Toggle Modal State
   * We are using useCallback as it returns memoized version of function
   * It will not cause infinite render of this component if used in useEffect.
   */
  const handleModalClose = useCallback(() => {
    setCrudType(null);
    setModalAlertState({ openAlert: false });
    methods.unregister();
  }, [methods, setCrudType]);

  /** SUCCESS AND ERROR ALERT HANDLERS --> */
  useEffect(() => {
    // QUERY ERROR
    if (stateTsrModalError) {
      handleModalClose();
      triggerAlert(stateTsrModalError, setAlertState);
    }
  }, [stateTsrModalError, handleModalClose, triggerAlert]);

  useEffect(() => {
    // ADD STATE TSR ERROR
    if (addStateTsrMethods.error) {
      triggerAlert(addStateTsrMethods.error, setModalAlertState);
    }

    // ADD STATE TSR SUCCESS
    if (addStateTsrMethods.isSuccess) {
      triggerAlert(
        CommonConstants.GLOBAL_MESSAGES.STATE_TSR_ADD,
        setAlertState,
        'success'
      );
      handleModalClose();
    }
  }, [
    methods,
    addStateTsrMethods.error,
    addStateTsrMethods.isSuccess,
    handleModalClose,
    triggerAlert,
  ]);

  useEffect(() => {
    // EDIT STATE TSR ERROR
    if (editStateTsrMethods.error) {
      triggerAlert(editStateTsrMethods.error, setModalAlertState);
    }

    // EDIT STATE TSR SUCCESS
    if (editStateTsrMethods.isSuccess) {
      triggerAlert(
        CommonConstants.GLOBAL_MESSAGES.STATE_TSR_EDIT,
        setAlertState,
        'success'
      );
      handleModalClose();
    }
  }, [
    editStateTsrMethods.isSuccess,
    editStateTsrMethods.error,
    handleModalClose,
    triggerAlert,
  ]);
  /** <-- Handle Error Alert */
  /** <-- SUCCESS AND ERROR ALERT HANDLERS */

  /** FORM HANDLERS --> */
  /** Form Submit Handler (Save Button Click) */
  const onSubmit: SubmitHandler<StateTsrModalData> = (formData) => {
    if (crudType === DialerCrudTypes.Add) {
      return addStateTsr({
        stateFullName: formData.stateFullName,
        state: formData.state,
        monFriStart: getStringFromDate(formData.monFriStart),
        monFriEnd: getStringFromDate(formData.monFriEnd),
        satStart: getStringFromDate(formData.satStart),
        satEnd: getStringFromDate(formData.satEnd),
        sunStart: getStringFromDate(formData.sunStart),
        sunEnd: getStringFromDate(formData.sunEnd),
        excludedDays: fromDateToDashedString(dateStates),
      });
    }
    if (crudType === DialerCrudTypes.Edit) {
      return editStateTsr({
        id: stateTsrId,
        stateFullName: formData.stateFullName,
        state: formData.state,
        monFriStart: getStringFromDate(formData.monFriStart),
        monFriEnd: getStringFromDate(formData.monFriEnd),
        satStart: getStringFromDate(formData.satStart),
        satEnd: getStringFromDate(formData.satEnd),
        sunStart: getStringFromDate(formData.sunStart),
        sunEnd: getStringFromDate(formData.sunEnd),
        excludedDays: fromDateToDashedString(dateStates),
      });
    }
  };

  /** Return Suitable Button for crudType */
  const getRestButton = () => {
    switch (crudType) {
      case DialerCrudTypes.View:
        return (
          <Button
            variant="outlined"
            color="secondary"
            onClick={(e) => {
              e.preventDefault();
              setCrudType(DialerCrudTypes.Edit);
            }}
          >
            {t('common.messages.Edit')}
          </Button>
        );
      default:
        return (
          <Button type="submit" onClick={methods.handleSubmit(onSubmit)}>
            {t('common.messages.Submit')}
          </Button>
        );
    }
  };

  /**
   * View CRUD, Edit CRUD - Resets Form Fields with the API DATA
   * Add CRUD - Empty the Form Fields
   */
  useEffect(() => {
    if (stateTsrData && crudType !== DialerCrudTypes.Add) {
      methods.reset({
        stateFullName: stateTsrData.stateFullName,
        state: stateTsrData.state,
        monFriStart: getDateFromString(stateTsrData.monFriStart),
        monFriEnd: getDateFromString(stateTsrData.monFriEnd),
        satStart: getDateFromString(stateTsrData.satStart),
        satEnd: getDateFromString(stateTsrData.satEnd),
        sunStart: getDateFromString(stateTsrData.sunStart),
        sunEnd: getDateFromString(stateTsrData.sunEnd),
      });
      setDateStates(getExcludedDayRanges(stateTsrData.excludedDays));
    } else if (crudType === DialerCrudTypes.Add) {
      methods.reset({
        stateFullName: '',
        state: '',
        monFriStart: null,
        monFriEnd: null,
        satStart: null,
        satEnd: null,
        sunStart: null,
        sunEnd: null,
      });

      setDateStates([
        {
          startDate: undefined,
          endDate: undefined,
          key: 'selection',
        },
      ]);
    }
  }, [methods, stateTsrData, crudType]);

  /** Handles confirmation click of modal and performs action accordingly */
  const handleConfirmationModalClick = () => {
    setIsConfirmationModalOpen(false);
    if (stateTsrId && deleteBtnOnClick) {
      handleModalClose();
      return deleteBtnOnClick(stateTsrId);
    }
  };

  return (
    <>
      <ConfirmationMessageModal
        headerText={t('common.messages.Warning')}
        isModalOpen={isConfirmationModalOpen}
        setIsModalOpen={setIsConfirmationModalOpen}
        message={t('adminSettings.Delete TSR Message', {
          interpolation: {
            escapeValue: false,
          },
          state: stateTsrData?.stateFullName,
        })}
        confirmButtonText={t('common.messages.Remove')}
        cancelButtonVariant="outlined"
        onClick={handleConfirmationModalClick}
      />
      <Modal
        open={!!crudType}
        onClose={handleModalClose}
        headerText={getHeaderText()}
        alertState={alertState}
      >
        <FormProvider {...methods}>
          <Box
            component="form"
            role="form"
            sx={{
              position: 'relative',
              maxHeight: '68vh',
              overflow: loading ? 'hidden' : 'auto',
            }}
          >
            <ScrollToTop dependencies={[loading]} />
            {loading ? (
              <FullScreenLoader
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  backgroundColor: 'common.white',
                  zIndex: 101,
                }}
              />
            ) : (
              <ModalAlert alertState={modalAlertState} />
            )}
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              sx={{
                width: '100%',
                height: '3.6rem',
                backgroundColor: '#F2F5F9',
                paddingLeft: '2.4rem',
                marginTop: '2rem',
              }}
            >
              <SvgIcon>{Icons.infoOutlined}</SvgIcon>
              <Typography variant="main">
                {t('adminSettings.State TSR Message')}
              </Typography>
            </Box>
            <Box
              display="flex"
              gap="3rem"
              justifyContent="space-between"
              sx={{ padding: '3.5rem 2.4rem 2.2rem' }}
            >
              <TextFieldWrapper
                name="stateFullName"
                variant="outlined"
                label={`${t('adminSettings.State Full Name')} *`}
                placeholder={t('adminSettings.State Full Name')}
                fullWidth
                size="small"
              />
              <TextFieldWrapper
                name="state"
                variant="outlined"
                label={`${t('adminSettings.State Short Name')} *`}
                placeholder={t('adminSettings.State Short Name')}
                fullWidth
                size="small"
              />
            </Box>
            <Box
              display="flex"
              gap="0.8rem"
              justifyContent="space-between"
              alignItems="center"
              sx={{ padding: '1.5rem 2.4rem 2.2rem' }}
            >
              <TimePickerCustom
                name="monFriStart"
                label={`${t('adminSettings.Weekdays Start Time')} *`}
                size="small"
              />
              <Box
                sx={{ borderTop: '0.2rem solid #767676', minWidth: '1.4rem' }}
              />
              <TimePickerCustom
                name="monFriEnd"
                label={`${t('adminSettings.Weekdays End Time')} *`}
                size="small"
              />
            </Box>
            <Box
              display="flex"
              gap="0.8rem"
              justifyContent="space-between"
              alignItems="center"
              sx={{ padding: '1.5rem 2.4rem 2.2rem' }}
            >
              <TimePickerCustom
                name="satStart"
                label={`${t('adminSettings.Saturday Start Time')} *`}
                size="small"
              />
              <Box
                sx={{ borderTop: '0.2rem solid #767676', minWidth: '1.4rem' }}
              />
              <TimePickerCustom
                name="satEnd"
                label={`${t('adminSettings.Saturday End Time')} *`}
                size="small"
              />
            </Box>
            <Box
              display="flex"
              gap="0.8rem"
              justifyContent="space-between"
              alignItems="center"
              sx={{ padding: '1.5rem 2.4rem 2.2rem' }}
            >
              <TimePickerCustom
                name="sunStart"
                label={t('adminSettings.Sunday Start Time')}
                size="small"
              />
              <Box
                sx={{ borderTop: '0.2rem solid #767676', minWidth: '1.4rem' }}
              />
              <TimePickerCustom
                name="sunEnd"
                label={t('adminSettings.Sunday End Time')}
                size="small"
              />
            </Box>
            <Box sx={{ padding: '1.5rem 2.4rem 0.5rem' }}>
              {dateStates.map((dateState, index) => (
                <Box
                  sx={{ maxWidth: '50%', padding: '0 1.5rem 1.5rem 0' }}
                  key={index}
                >
                  <DateRangePickerController
                    key={index}
                    dateState={dateState}
                    setDateState={(newDateState) => {
                      const updatedDateStates = [...dateStates];
                      updatedDateStates[index] =
                        typeof newDateState === 'function'
                          ? (
                              newDateState as (
                                prevState: DateState
                              ) => DateState
                            )(updatedDateStates[index])
                          : (newDateState as DateState);
                      setDateStates(updatedDateStates);
                    }}
                    name={`dateRangePicker${index}`}
                    placeholder={t('common.dateRange.Exclude Specific Dates')}
                    size="small"
                  />
                </Box>
              ))}
            </Box>
            <Box sx={{ padding: '0 2.4rem 1rem' }}>
              <Button
                variant="text"
                color="secondary"
                onClick={addDateRangePicker}
                sx={{
                  textTransform: 'capitalize',
                  paddingX: '0',
                  '&:hover': {
                    backgroundColor: 'white',
                  },
                }}
                icon="add"
              >
                {t('adminSettings.Add Days To Exclude')}
              </Button>
            </Box>
            <Box borderTop="0.1rem solid #A3ABB2">
              <Box
                display="flex"
                alignItems="center"
                sx={{ padding: '3rem 2.4rem 2.4rem' }}
              >
                {crudType !== DialerCrudTypes.Add && (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => {
                      setIsConfirmationModalOpen(true);
                    }}
                  >
                    {t('common.messages.Delete')}
                  </Button>
                )}
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => setCrudType(null)}
                  sx={{ ml: 'auto', mr: '1.6rem' }}
                >
                  {t('common.messages.Cancel')}
                </Button>
                {getRestButton()}
              </Box>
            </Box>
          </Box>
        </FormProvider>
      </Modal>
    </>
  );
};

export default StateTsrModal;
